import { useState, useEffect, useRef, ChangeEvent } from 'react';
import { toast } from 'react-toastify';
import { getApiUrl, getApiHeaders } from '../utils/api';
import './InventoryPage.css';

interface InventoryItem {
    id: string;
    code: string;
    name: string;
    quantity: number;
    unit: string;
    unitCost: number | null;
    totalCost: number | null;
    currency: string;
    source: 'delivery' | 'materials' | 'manual' | 'photo';
    addedAt: string;
    supplier?: string;
}

interface CountedItem {
    name: string;
    count: number;
    confidence: number;
}

interface CountResult {
    items: CountedItem[];
    totalCount: number;
    confidence: number;
}

function InventoryPage() {
    const [inventory, setInventory] = useState<InventoryItem[]>([]);
    const [image, setImage] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [countResult, setCountResult] = useState<CountResult | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const cameraInputRef = useRef<HTMLInputElement>(null);

    const loadInventory = async () => {
        try {
            const response = await fetch(getApiUrl('/api/inventory/items'), {
                headers: await getApiHeaders()
            });

            if (response.ok) {
                const data = await response.json();
                setInventory(data.items || []);
            }
        } catch (error) {
            console.error('Failed to load inventory:', error);
        }
    };

    useEffect(() => {
        loadInventory();
    }, []);

    const saveToBackend = async (items: Partial<InventoryItem>[], source: string) => {
        try {
            const apiHeaders = await getApiHeaders();
            const response = await fetch(getApiUrl('/api/inventory/items'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...apiHeaders
                },
                body: JSON.stringify({ items, source })
            });

            if (!response.ok) throw new Error('Failed to save');
            await loadInventory();
            return true;
        } catch (error) {
            console.error('Failed to save inventory:', error);
            return false;
        }
    };

    const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const preview = URL.createObjectURL(file);
        setImage(preview);
        processInventoryPhoto(file);
    };

    const processInventoryPhoto = async (file: File) => {
        setIsProcessing(true);

        try {
            const formData = new FormData();
            formData.append('image', file);

            const response = await fetch(getApiUrl('/api/inventory/count-from-photo'), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('artisan_token')}`
                },
                body: formData
            });

            if (!response.ok) {
                throw new Error('Failed to count items');
            }

            const data = await response.json();
            setCountResult(data.count);
            toast.success(`Found ${data.count.totalCount} items!`);
        } catch (error) {
            console.error('Count error:', error);
            toast.error('Failed to process image. Please try again.');
            setImage(null);
        } finally {
            setIsProcessing(false);
        }
    };

    const confirmCount = async () => {
        if (!countResult) return;

        const newItems = countResult.items.map((item, index) => ({
            code: `PHT-${Date.now().toString(36).toUpperCase()}-${index}`,
            name: item.name,
            quantity: item.count,
            unit: 'units',
            unitCost: null,
            totalCost: null,
            currency: 'EUR'
        }));

        const success = await saveToBackend(newItems, 'photo');
        if (success) {
            toast.success('Stock count added to inventory!');
            reset();
        } else {
            toast.error('Failed to save inventory');
        }
    };

    const reset = () => {
        if (image) URL.revokeObjectURL(image);
        setImage(null);
        setCountResult(null);
    };

    const getTotalValue = () => {
        return inventory.reduce((sum, item) => {
            return sum + (item.quantity * (item.unitCost || 0));
        }, 0);
    };

    const getSourceIcon = (source: string) => {
        switch (source) {
            case 'delivery': return '📄';
            case 'materials': return '🧱';
            case 'photo': return '📷';
            default: return '✏️';
        }
    };

    const deleteItem = async (id: string) => {
        try {
            const response = await fetch(getApiUrl(`/api/inventory/items/${id}`), {
                method: 'DELETE',
                headers: await getApiHeaders()
            });

            if (response.ok) {
                await loadInventory();
                toast.success('Item removed from inventory');
            } else {
                toast.error('Failed to delete item');
            }
        } catch (error) {
            console.error('Delete error:', error);
            toast.error('Failed to delete item');
        }
    };

    return (
        <div className="inventory-page animate-fade-in">
            <h1 className="page-title">
                <span className="title-icon">📦</span>
                Stock Count
            </h1>
            <p className="page-subtitle">
                Take a photo of your products to instantly sync stock levels
            </p>

            {!image && (
                <div className="scan-section animate-slide-up">
                    <div
                        className="upload-zone"
                        onClick={() => cameraInputRef.current?.click()}
                        onDragOver={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                        onDrop={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const file = e.dataTransfer.files?.[0];
                            if (file) {
                                const preview = URL.createObjectURL(file);
                                setImage(preview);
                                processInventoryPhoto(file);
                            }
                        }}
                    >
                        <span className="upload-icon">📷</span>
                        <h3>Photograph Your Stock</h3>
                        <p>Take a photo or Drag & Drop to count items</p>
                    </div>

                    <div className="camera-buttons">
                        <button
                            className="btn btn-primary btn-large"
                            onClick={() => cameraInputRef.current?.click()}
                        >
                            <span>📷</span>
                            Take Photo
                        </button>
                        <button
                            className="btn btn-secondary btn-large"
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <span>🖼️</span>
                            Upload
                        </button>
                    </div>

                    <input
                        ref={cameraInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                    />
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                    />
                </div>
            )}

            {isProcessing && (
                <div className="processing-section animate-fade-in">
                    <div className="processing-animation">
                        <div className="loader-ring"></div>
                        <span className="processing-icon">🔍</span>
                    </div>
                    <h2>Counting Items...</h2>
                    <p>AI is analyzing your photo to count products</p>
                </div>
            )}

            {image && countResult && !isProcessing && (
                <div className="result-section animate-slide-up">
                    <div className="scanned-image">
                        <img src={image} alt="Stock photo" />
                    </div>

                    <div className="card extracted-data">
                        <div className="data-header">
                            <div>
                                <span className="doc-type">Photo Count</span>
                                <h3 className="doc-number">{countResult.totalCount} items detected</h3>
                            </div>
                            <div className="confidence-badge">
                                <span className="confidence-value">{Math.round(countResult.confidence * 100)}%</span>
                                <span className="confidence-label">Confidence</span>
                            </div>
                        </div>

                        <div className="items-section">
                            <h4>Items Found</h4>
                            <div className="items-list">
                                {countResult.items.map((item, index) => (
                                    <div key={index} className="item-row">
                                        <div className="item-info">
                                            <span className="item-name">{item.name}</span>
                                        </div>
                                        <div className="item-quantity">
                                            <span className="qty-value">{item.count}</span>
                                            <span className="qty-unit">units</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="action-buttons">
                        <button className="btn btn-secondary" onClick={reset}>
                            ← Take Another
                        </button>
                        <button className="btn btn-primary" onClick={confirmCount}>
                            ✓ Add to Inventory
                        </button>
                    </div>
                </div>
            )}

            <div className="current-stock-section">
                <div className="section-header">
                    <h2 className="section-title">Current Inventory</h2>
                    <div className="inventory-summary">
                        <span className="summary-item">
                            <strong>{inventory.length}</strong> items
                        </span>
                        <span className="summary-item">
                            <strong>€{getTotalValue().toFixed(2)}</strong> value
                        </span>
                    </div>
                </div>

                {inventory.length === 0 ? (
                    <div className="empty-state">
                        <span className="empty-icon">📦</span>
                        <h3>No products in stock yet</h3>
                        <p>Scan a delivery note, materials invoice, or take a photo to start tracking</p>
                    </div>
                ) : (
                    <div className="inventory-list">
                        {inventory.map((item) => (
                            <div key={item.id} className="inventory-item">
                                <span className="item-source">{getSourceIcon(item.source)}</span>
                                <div className="item-details">
                                    <span className="item-name">{item.name}</span>
                                    <span className="item-code">{item.code}</span>
                                </div>
                                <div className="item-qty">
                                    <span className="qty-value">{item.quantity}</span>
                                    <span className="qty-unit">{item.unit}</span>
                                </div>
                                {item.unitCost && (
                                    <span className="item-cost">
                                        {item.currency} {item.unitCost.toFixed(2)}
                                    </span>
                                )}
                                <button
                                    className="btn-delete"
                                    onClick={() => deleteItem(item.id)}
                                    title="Remove item"
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default InventoryPage;
